import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Select } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { useHubProjects } from '../hub/api';
import { useOrgSettings } from '../settings/api';
import { HubProjectPicker } from './HubProjectPicker';
import {
  useCreateAgreement,
  useAgreementClients,
  useUpdateAgreement,
  type BillBy,
  type AgreementCore,
  type AgreementInput,
  type AgreementType,
} from './api';

const TYPE_OPTIONS: Array<{ value: AgreementType; label: string }> = [
  { value: 'PROJECT', label: 'Project work' },
  { value: 'SUPPORT', label: 'Support' },
  { value: 'RETAINER', label: 'Retainer' },
  { value: 'HOSTING', label: 'Hosting' },
  { value: 'INTERNAL', label: 'Internal' },
];

const BILL_BY_OPTIONS: Array<{ value: BillBy; label: string }> = [
  { value: 'HOURS', label: 'Hours × rate' },
  { value: 'FIXED', label: 'Fixed fee' },
  { value: 'NONE', label: 'Not billable' },
];

/**
 * What a CREATE may be opened holding. The invoice builder opens it already
 * on the client being invoiced and the type the invoice implies (support for
 * prepaid hours, a fixed-fee project for installments), so making the
 * agreement costs a name and a click rather than re-entering what the screen
 * behind the modal already knows. Every field stays editable.
 */
export type AgreementFormDefaults = Partial<{
  name: string;
  clientId: string;
  type: AgreementType;
  billBy: BillBy;
  hourlyRate: string;
  budgetHours: string;
  budgetAmount: string;
  startDate: string;
  endDate: string;
}>;

interface FormState {
  name: string;
  clientId: string;
  code: string;
  type: AgreementType;
  billBy: BillBy;
  hourlyRate: string;
  budgetHours: string;
  budgetAmount: string;
  startDate: string;
  endDate: string;
}

const EMPTY: FormState = {
  name: '',
  clientId: '',
  code: '',
  type: 'PROJECT',
  billBy: 'HOURS',
  hourlyRate: '',
  budgetHours: '',
  budgetAmount: '',
  startDate: '',
  endDate: '',
};

function fromAgreement(p: AgreementCore): FormState {
  return {
    name: p.name,
    clientId: p.client.id,
    code: p.code ?? '',
    type: p.type,
    billBy: p.billBy,
    hourlyRate: p.hourlyRate != null ? String(p.hourlyRate) : '',
    budgetHours: p.budgetHours != null ? String(p.budgetHours) : '',
    budgetAmount: p.budgetAmount != null ? String(p.budgetAmount) : '',
    startDate: p.startDate ?? '',
    endDate: p.endDate ?? '',
  };
}

export function AgreementModal({
  open,
  onOpenChange,
  agreement,
  defaults,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pass an existing agreement to edit; omit to create. */
  agreement?: AgreementCore;
  /** Create only: what the form opens holding. Ignored when editing. */
  defaults?: AgreementFormDefaults;
  onSaved?: (id: string) => void;
}) {
  const isEdit = !!agreement;
  const clientsQuery = useAgreementClients();
  const create = useCreateAgreement();
  const update = useUpdateAgreement(agreement?.id ?? '');
  const pending = create.isPending || update.isPending;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [hubProjectId, setHubProjectId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; clientId?: string; hubProjectId?: string }>({});

  // Org policy: a NEW agreement must name the hub project the work lives in.
  // Edit is exempt here — an existing bare agreement is linked from its own
  // page, where the required banner points at the fix.
  const requireLink = useOrgSettings().data?.requireHubLink === true && !isEdit;
  const hub = useHubProjects();

  useEffect(() => {
    if (open) {
      setForm(agreement ? fromAgreement(agreement) : { ...EMPTY, ...defaults });
      setHubProjectId('');
      setError(null);
      setFieldErrors({});
      create.reset();
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agreement?.id]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const clientOptions = useMemo(
    () =>
      (clientsQuery.data?.clients ?? [])
        .filter((c) => c.isActive !== false || c.id === form.clientId)
        .map((c) => ({ value: c.id, label: c.name })),
    [clientsQuery.data, form.clientId],
  );

  const num = (v: string): number | null => (v.trim() === '' ? null : Number(v));

  const submit = async () => {
    const errs: typeof fieldErrors = {};
    if (!form.name.trim()) errs.name = 'Agreement name is required';
    if (!form.clientId) errs.clientId = 'Pick a client';
    if (requireLink && !hubProjectId) {
      errs.hubProjectId = 'This organization requires every agreement to be linked to a hub project';
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    const input: AgreementInput = {
      name: form.name.trim(),
      clientId: form.clientId,
      code: form.code.trim() || null,
      type: form.type,
      billBy: form.billBy,
      hourlyRate: num(form.hourlyRate),
      budgetHours: num(form.budgetHours),
      budgetAmount: num(form.budgetAmount),
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      ...(requireLink && hubProjectId ? { hubProjectId } : {}),
    };
    setError(null);
    try {
      const result = isEdit ? await update.mutateAsync(input) : await create.mutateAsync(input);
      onOpenChange(false);
      onSaved?.(result.agreement.id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed — try again.');
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Edit agreement' : 'New agreement'}
      description={
        isEdit ? undefined : 'Agreements group tracked time, budgets, and billing for a client.'
      }
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={pending}>
            {isEdit ? 'Save changes' : 'Create agreement'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Agreement name"
          placeholder="e.g. Website Redesign"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          error={fieldErrors.name}
          className="sm:col-span-2"
          autoFocus
        />
        <Select
          label="Client"
          placeholder={clientsQuery.isLoading ? 'Loading clients…' : 'Select a client'}
          options={clientOptions}
          value={form.clientId}
          onChange={(e) => set('clientId', e.target.value)}
          error={fieldErrors.clientId}
        />
        <Input
          label="Code"
          placeholder="Optional, e.g. ACME-01"
          value={form.code}
          onChange={(e) => set('code', e.target.value)}
        />
        {/* Org policy: a new agreement names the hub project its work lives
            in, right here — creating first and linking later is a bare row
            the required banner then has to chase. */}
        {requireLink && (
          <div className="space-y-3 sm:col-span-2">
            <HubProjectPicker rows={hub.data?.projects ?? []} value={hubProjectId} onChange={setHubProjectId} clientId={form.clientId} />
            {fieldErrors.hubProjectId ? (
              <p className="text-body-sm text-status-late">{fieldErrors.hubProjectId}</p>
            ) : hub.data?.connected === false ? (
              <p className="text-body-sm text-status-late">
                The hub connection is down, and this organization requires the link — fix
                Settings → Hub first, or turn the requirement off there.
              </p>
            ) : (
              <p className="text-body-sm text-grey-500">
                Required by this organization: every agreement is linked to the hub project the
                work happens in. A hub project that already has an agreement can take another —
                that is how a follow-on support agreement rolls over.
              </p>
            )}
          </div>
        )}
        <Select
          label="Type"
          options={TYPE_OPTIONS}
          value={form.type}
          onChange={(e) => set('type', e.target.value as AgreementType)}
        />
        <Select
          label="Bill by"
          options={BILL_BY_OPTIONS}
          value={form.billBy}
          onChange={(e) => set('billBy', e.target.value as BillBy)}
        />
        {form.billBy === 'HOURS' && (
          <Input
            label="Hourly rate ($)"
            type="number"
            min="0"
            step="5"
            placeholder="e.g. 165"
            value={form.hourlyRate}
            onChange={(e) => set('hourlyRate', e.target.value)}
            hint="Default rate; person overrides apply on Team."
          />
        )}
        <Input
          label="Budget hours"
          type="number"
          min="0"
          placeholder="e.g. 400"
          value={form.budgetHours}
          onChange={(e) => set('budgetHours', e.target.value)}
        />
        <Input
          label={form.type === 'RETAINER' || form.type === 'HOSTING' ? 'Budget amount ($/month)' : 'Budget amount ($)'}
          type="number"
          min="0"
          placeholder="e.g. 42000"
          value={form.budgetAmount}
          onChange={(e) => set('budgetAmount', e.target.value)}
          hint={form.budgetHours ? 'Hours budget takes precedence for burn.' : undefined}
        />
        <Input
          label="Start date"
          type="date"
          value={form.startDate}
          onChange={(e) => set('startDate', e.target.value)}
        />
        <Input
          label="End date"
          type="date"
          value={form.endDate}
          onChange={(e) => set('endDate', e.target.value)}
        />
      </div>
      {error && (
        <p className="mt-4 rounded-lg bg-status-late-bg px-3 py-2 text-body-sm text-status-late">
          {error}
        </p>
      )}
    </Modal>
  );
}
